/**
 * useStarPersona — TanStack Query hook for the user's Star Persona gallery.
 *
 * Endpoint: GET /api/v1/star-persona
 * Response: { generic: { status, frames[] }, selfie: { status, frames[] }, selectedR2Key }
 *
 * `useSelectStarPersona` posts to /star-persona/select and invalidates both
 * the gallery and `useMe` (so the avatar masthead picks up profileAvatarR2Key).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

export interface StarPersonaFrame {
  r2Key: string;
  url: string;
  archetype: string | null;
}

export interface StarPersonaResponse {
  generic: { status: 'pending' | 'ready'; frames: StarPersonaFrame[] };
  selfie: { status: 'pending' | 'ready' | 'absent'; frames: StarPersonaFrame[] };
  selectedR2Key: string | null;
}

export function useStarPersona(): {
  data: StarPersonaResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const jwt = useAuth((s) => s.jwt);
  const query = useQuery<StarPersonaResponse, Error>({
    queryKey: ['star-persona'],
    queryFn: async () => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await apiFetch('/api/v1/star-persona', jwt);
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`star_persona_failed: ${res.status} ${body}`);
      }
      return (await res.json()) as StarPersonaResponse;
    },
    enabled: !!jwt,
    staleTime: 30_000,
  });
  return { data: query.data, isLoading: query.isLoading, isError: query.isError, refetch: query.refetch };
}

export function useDeleteStarPersonaFrame(): {
  mutateAsync: (r2Key: string) => Promise<{ ok: boolean }>;
  isPending: boolean;
  isError: boolean;
} {
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();
  const m = useMutation<{ ok: boolean }, Error, string>({
    mutationFn: async (r2Key) => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await apiFetch('/api/v1/star-persona/frame', jwt, {
        method: 'DELETE',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ r2Key }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`star_persona_delete_failed: ${res.status} ${body}`);
      }
      return (await res.json()) as { ok: boolean };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['star-persona'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
  return { mutateAsync: m.mutateAsync, isPending: m.isPending, isError: m.isError };
}

export function useSelectStarPersona(): {
  mutate: (r2Key: string) => void;
  mutateAsync: (r2Key: string) => Promise<{ ok: boolean; onboardingStep: number }>;
  isPending: boolean;
  isError: boolean;
} {
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();
  const m = useMutation<{ ok: boolean; onboardingStep: number }, Error, string>({
    mutationFn: async (r2Key) => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await apiFetch('/api/v1/star-persona/select', jwt, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ r2Key }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`star_persona_select_failed: ${res.status} ${body}`);
      }
      return (await res.json()) as { ok: boolean; onboardingStep: number };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['star-persona'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
  return { mutate: m.mutate, mutateAsync: m.mutateAsync, isPending: m.isPending, isError: m.isError };
}
